export type LanguageLike = {
  id?: number | string;
  languageId?: number | string;
  name?: string;
  display?: string;
  Display?: string;
  abbrev?: string;
  ietf?: string;
  Ietf?: string;
  IETF?: string;
  supported?: boolean;
  Supported?: boolean;
  active?: boolean;
  Active?: boolean;
};

export function isSupportedLanguage(language: LanguageLike | null | undefined): boolean {
  if (!language) return false;
  if (typeof language.supported === "boolean") return language.supported;
  if (typeof language.Supported === "boolean") return language.Supported;
  return true;
}

export function isActiveLanguage(language: LanguageLike | null | undefined): boolean {
  if (!language) return false;
  if (!isSupportedLanguage(language)) return false;
  if (typeof language.active === "boolean") return language.active;
  if (typeof language.Active === "boolean") return language.Active;
  return true;
}

export function filterSupportedLanguages(languages: LanguageLike[] = []): LanguageLike[] {
  return languages.filter((lang) => isSupportedLanguage(lang));
}

export function filterActiveLanguages(languages: LanguageLike[] = []): LanguageLike[] {
  return languages.filter((lang) => isActiveLanguage(lang));
}

export const DEFAULT_LANGUAGE_ID = 1;

export function getLanguageId(language: LanguageLike | null | undefined): number {
  const raw = language?.id ?? language?.languageId ?? 0;
  const numeric = Number(raw);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

export function getLanguageAbbrev(language: LanguageLike | null | undefined): string {
  return String(language?.abbrev || "").trim().toLowerCase();
}

export function getLanguageIetf(language: LanguageLike | null | undefined, fallback = "en"): string {
  const raw = String(language?.ietf || language?.Ietf || language?.IETF || "").trim();
  if (raw) return raw;

  const abbrev = getLanguageAbbrev(language);
  if (!abbrev) return fallback;

  switch (abbrev) {
    case "eng":
      return "en";
    case "esp":
      return "es-MX";
    case "spa":
      return "es-ES";
    case "fr":
    case "fra":
      return "fr";
    case "frq":
      return "fr-CA";
    case "de":
    case "deu":
      return "de";
    case "nl":
    case "nld":
      return "nl";
    case "por":
      return "pt";
    case "zhs":
      return "zh-Hans";
    case "zht":
      return "zh-Hant";
    default:
      return abbrev;
  }
}

export function getTranslationLocale(language: LanguageLike | null | undefined, fallback = "en"): string {
  return getLanguageIetf(language, fallback);
}

/** Normalize catalog rows / section editor defs so /Translate gets correct source and target locales. */
export function toTranslationLanguageLike(
  language: LanguageLike | null | undefined,
  abbrevFallback = "",
): LanguageLike | null {
  if (!language && !abbrevFallback) return null;

  const abbrev = getLanguageAbbrev(language)
    || String(abbrevFallback || (language as { key?: string })?.key || "").trim().toLowerCase();
  if (!abbrev) return language ?? null;

  return { ...language, abbrev };
}

export function isDefaultLanguageId(languageId: number | string | null | undefined): boolean {
  const numeric = Number(languageId || DEFAULT_LANGUAGE_ID);
  return !Number.isFinite(numeric) || numeric <= 1;
}

export function resolveLanguageIdFromCode(langCode: string | null | undefined, languages: LanguageLike[] = []): number {
  const normalized = String(langCode || "").trim().toLowerCase();
  if (!normalized) return 0;

  const byAbbrev = languages.find((lang) => getLanguageAbbrev(lang) === normalized);
  if (byAbbrev) return getLanguageId(byAbbrev);

  const byIetf = languages.find((lang) => getLanguageIetf(lang).toLowerCase() === normalized);
  if (byIetf) return getLanguageId(byIetf);

  if (normalized === "en" || normalized === "eng") return 1;
  if (normalized === "es" || normalized === "esp" || normalized === "es-mx") return 2;

  return 0;
}

export function resolveLanguageMeta(
  item: LanguageLike | null | undefined,
  languages: LanguageLike[] = []
): { key: string; languageId: number; languageName: string } | null {
  if (!item) return null;

  const directAbbrev = getLanguageAbbrev(item);
  const languageId = getLanguageId(item);

  if (directAbbrev) {
    return {
      key: directAbbrev,
      languageId: languageId || resolveLanguageIdFromAbbrev(directAbbrev, languages),
      languageName: getLanguagePickerLabel(item) || directAbbrev,
    };
  }

  if (languageId > 0) {
    const match = languages.find((lang) => getLanguageId(lang) === languageId);
    const abbrev = getLanguageAbbrev(match) || String(languageId);
    return {
      key: abbrev,
      languageId,
      languageName: getLanguagePickerLabel(match || item) || abbrev,
    };
  }

  return null;
}

export function resolveLanguageIdFromAbbrev(abbrev: string, languages: LanguageLike[] = []): number {
  const normalized = String(abbrev || "").trim().toLowerCase();
  if (!normalized) return 0;

  const match = languages.find((lang) => getLanguageAbbrev(lang) === normalized);
  if (match) return getLanguageId(match);

  if (normalized === "eng") return 1;
  if (normalized === "esp") return 2;
  return 0;
}

export function getLanguagePickerLabel(language: LanguageLike | null | undefined): string {
  const display = String(language?.display || language?.Display || "").trim();
  if (display) return display;

  const name = String(language?.name || "").trim();
  if (name) return name;

  const abbrev = getLanguageAbbrev(language);
  if (abbrev === "eng") return "English";
  if (abbrev === "esp" || abbrev === "spa") return "Spanish";
  if (abbrev === "frq") return "French (Quebec)";
  if (abbrev === "zhs") return "Chinese (Simplified)";
  if (abbrev === "zht") return "Chinese (Traditional)";
  return abbrev || "Language";
}

/** Internal/admin registry label (Name + optional IETF disambiguation). */
export function getLanguageRegistryName(language: LanguageLike | null | undefined): string {
  const ietf = String(language?.ietf || language?.Ietf || language?.IETF || "").trim();
  const name = String(language?.name || "").trim();

  let registryName = name;
  if (!registryName) {
    registryName = getLanguagePickerLabel(language);
  }

  if (ietf && !registryName.includes(ietf)) {
    return `${registryName} [${ietf}]`;
  }

  return registryName;
}

export function getLanguageAdminLabel(language: LanguageLike | null | undefined): string {
  const name = getLanguageRegistryName(language);
  if (!isActiveLanguage(language) && isSupportedLanguage(language)) {
    return `${name} (dev)`;
  }
  return name;
}

/** End-user picker label; falls back to registry name when Display is unset. */
export function getLanguageDisplayName(language: LanguageLike | null | undefined): string {
  return getLanguagePickerLabel(language);
}

export function getFirstNonEnglishLanguageId(languages: LanguageLike[] = []): number {
  const match = languages.find((lang) => !isDefaultLanguageId(getLanguageId(lang)));
  return match ? getLanguageId(match) : 0;
}

export function getSectionLanguageContent(
  section: Record<string, unknown> | null | undefined,
  abbrev: string,
  languages: LanguageLike[] = []
): unknown {
  if (!section) return null;

  const normalizedAbbrev = String(abbrev || "").trim().toLowerCase();
  const targetLanguageId = resolveLanguageIdFromAbbrev(normalizedAbbrev, languages);
  const languageContents = section.languageContents ?? section.LanguageContents;
  if (Array.isArray(languageContents)) {
    const match = languageContents.find((item: LanguageLike & { content?: unknown; Content?: unknown }) => {
      const itemAbbrev = getLanguageAbbrev(item).toLowerCase();
      const itemLanguageId = getLanguageId(item);
      if (normalizedAbbrev && itemAbbrev === normalizedAbbrev) return true;
      if (targetLanguageId > 0 && itemLanguageId === targetLanguageId) return true;
      return false;
    });

    if (match) {
      const content = (match as { content?: unknown; Content?: unknown }).content
        ?? (match as { content?: unknown; Content?: unknown }).Content;
      if (content != null && content !== "") return content;
    }
  }

  return null;
}

export function isNonEnglishLanguage(language: LanguageLike | null | undefined): boolean {
  const languageId = getLanguageId(language);
  if (languageId > 0) return !isDefaultLanguageId(languageId);

  const abbrev = getLanguageAbbrev(language);
  return !!abbrev && abbrev !== "eng";
}

export function isSingleNonEnglishLanguageTestType(languages: unknown): boolean {
  if (!Array.isArray(languages) || languages.length !== 1) return false;

  const onlyLanguage = languages[0];
  if (typeof onlyLanguage === "object" && onlyLanguage !== null) {
    return isNonEnglishLanguage(onlyLanguage as LanguageLike);
  }

  const numeric = Number(onlyLanguage);
  return Number.isFinite(numeric) && numeric > 1;
}

/** @deprecated Use isNonEnglishLanguage for language-agnostic checks. */
export function isSpanishLanguage(language: LanguageLike | null | undefined): boolean {
  const abbrev = getLanguageAbbrev(language);
  const name = String(language?.name || "").trim().toLowerCase();
  return abbrev === "es" || abbrev === "esp" || abbrev === "spa" || name.includes("spanish") || name.includes("espa");
}

export function isNonEnglishSectionLang(langCode: string | null | undefined): boolean {
  const normalized = String(langCode || "").trim().toLowerCase();
  return !!normalized && normalized !== "eng";
}
